// @flow
import * as React from "react";
import _Select, { components } from "react-select";
import { List as _List, AutoSizer } from "react-virtualized";
import styled, { css, ThemeConsumer } from "styled-components";
import Media from "react-media";
import Gettext from "../common/Gettext";
import { fontSize, rtl } from "../common/styled-mixins";
import { FullPage } from "../Fillup/ModalPromo";
import _Flag from "../common/Flag";

import { InfoIcon } from "./Icons";

const PhoneInfo = styled.div`
  display: flex;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: stretch;
  width: 100%;
  margin-top: 8px;
  font-size: ${fontSize(12)};
  line-height: 14px;
  color: #a7a7a7;
  ${rtl("text-align", "left")};

  @media (min-width: 960px) {
    width: 304px;
    ${rtl("margin-left", "212px")};
    margin-left: 212px;
  }
`;

const IconContainer = styled.div`
  width: 16px;
  min-width: 16px;
  ${rtl("margin-right", "7px")};
`;

export const Info = ({ children }: { children: React.Node }) => (
  <PhoneInfo>
    <IconContainer>
      <InfoIcon verification />
    </IconContainer>
    {children}
  </PhoneInfo>
);

const SelectCode = styled.div`
  font-size: inherit;
  color: inherit;
  ${rtl("margin-left", "5px")};

  ${({ version }) =>
    version === 2 &&
    css`
      font-weight: 700;
      color: #919191;
    `};
`;

const Select = styled(_Select)`
  position: static !important;
  width: 100%;
  font-weight: 400;
  line-height: 15px;

  :focus {
    outline: none;
  }

  .react-select__control {
    height: 39px;
    font-weight: 400;
    font-size: ${fontSize(16)};
    line-height: 15px;
    color: #a7a7a7;
    background-color: rgb(255, 255, 255);
    ${rtl("border-radius", "3px 0px 0px 3px", "0px 3px 3px 0px")};
    border: 1px solid rgb(204, 204, 204);
    ${rtl("border-right", "none")};
    box-shadow: inset 0px 2px 0px 0px rgb(235, 235, 235);

    :hover {
      box-shadow: inset 0px 2px 0px 0px rgb(235, 235, 235);
    }

    ${props =>
      props.isWithdraw &&
      css`
        box-shadow: none;
        &:hover {
          box-shadow: none;
        }
      `}

    @media (max-width: 959px) {
      font-size: ${fontSize(14)};
      height: 39px;
      min-height: 0;
      ${props =>
        props.isWithdraw &&
        css`
          height: 39px;
        `}
    }

    ${({ theme, isProfile, isWithdraw }) =>
      theme.newIndex &&
      !isProfile &&
      !isWithdraw &&
      css`
        min-height: 50px;
        color: #fff;
        background-color: rgba(255, 255, 255, 0.25);
        border-color: transparent;
        ${rtl("border-radius", "25px 0 0 25px", "0 25px 25px 0")};
        box-shadow: none;

        :hover,
        :focus,
        :active {
          border-color: transparent;
          box-shadow: none;
        }

        @media (max-width: 959px) {
          min-height: 40px;
        }

        ${({ menuIsOpen }) =>
          menuIsOpen &&
          css`
            background-color: rgba(255, 255, 255, 0.25) !important;
            border-color: #fff;
            border-bottom-color: transparent;
            ${rtl("border-radius", "25px 0 0 0", "0 25px 0 0")};

            :hover,
            :focus,
            :active {
              border-color: #fff;
              border-bottom-color: transparent;
            }
          `};
      `};
  }

  .react-select__indicator-separator {
    display: none;
  }

  .react-select__menu {
    margin-top: 0;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }

  .react-select__dropdown-indicator {
    padding: 0;
    ${rtl("padding-left", 0)};
    ${rtl("padding-right", "8px")};

    ${({ theme, isProfile, isWithdraw }) =>
      theme.newIndex &&
      !isProfile &&
      !isWithdraw &&
      css`
        color: #fff;
        opacity: 0.5;

        :hover {
          color: #fff;
          opacity: 1;
        }

        ${({ menuIsOpen }) =>
          menuIsOpen &&
          css`
            color: #fff;
            opacity: 1;
          `};
      `};
  }
`;

const Flag = styled(_Flag)`
  flex-grow: 0;
  flex-shrink: 0;
  height: 100%;
  @media (max-width: 959px) {
    display: none !important;
  }
`;

const Menu = styled.div`
  width: 100%;
  height: 200px;
  max-height: 200px;
  z-index: 20;
  background: #fff;
  border: 1px solid #cccccc;
  border-top: none;
  border-radius: 4px;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  overflow: hidden;
`;

const OptionInner = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  align-content: flex-start;
  justify-content: flex-start;
  ${rtl("text-align", "left")};
`;

const ValueOption = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  width: 100%;
  font-size: inherit;
  line-height: inherit;
  cursor: pointer;
`;

const Option = styled(ValueOption)`
  direction: ${({ theme }) => theme.direction};
  padding: 8px 10px;
  color: #343434;

  ${SelectCode} {
    color: #a7a7a7;
  }

  @media (max-width: 959px) {
    border-bottom: 1px solid #ccc;
  }

  :hover {
    color: #fff;
    background-color: #3498db;

    ${OptionInner}, ${SelectCode} {
      color: #fff;
    }
  }

  ${({ isFocused }) =>
    isFocused &&
    css`
      color: #fff;
      background-color: #3498db;

      ${OptionInner}, ${SelectCode} {
        color: #fff;
      }
    `};
`;

const OptionContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  font-size: ${fontSize(14)};
`;

const OptionLabel = styled.span`
  direction: ltr;
  font-size: 14px;
`;

const MobileContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
  width: 100%;
  height: 100%;
  padding-left: 15px;
  padding-right: 15px;
`;

const MobileHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 50px;
`;

const SizerContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
`;

const HeaderText = styled.div`
  font-size: ${fontSize(16)};
  line-height: 1.2;
  color: #343434;
  text-align: center;
`;

const BackButton = styled.button`
  width: 16px;
  height: 16px;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
`;

const CloseButton = styled.button`
  width: 20px;
  min-width: 20px;
  height: 20px;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
`;

const InfoContainer = styled.div`
  padding-bottom: 10px;
`;

const List = styled(_List)`
  &&&:hover,
  &&&:focus,
  &&&:active {
    outline: none;
  }
`;

type Country = *;

type Props = {
  name?: string,
  value: Country,
  options: Array<Country>,
  desktop?: boolean,
  isProfile: boolean,
  showInfo?: boolean,
  error?: boolean,
  onChange: (target: Object) => void
};

const Placeholder = ({ isProfile }: { isProfile: boolean }) => (
  <ValueOption isWorld={true} isProfile={isProfile}>
    <Gettext>Country/Region</Gettext>
  </ValueOption>
);

function ValueView({ innerProps, data, isProfile, version }) {
  const { label, code, phoneCode } = data;

  let value = code === "world" ? label : `+${phoneCode}`;
  return (
    <ValueOption {...innerProps}>
      <Flag country={code} />
      <SelectCode version={version} isProfile={isProfile}>
        {value}
      </SelectCode>
    </ValueOption>
  );
}

type MobileMenuProps = {|
  onDismiss: () => void,
  children: React.Node,
  showInfo?: React.Node
|};

const MobileMenu = (props: MobileMenuProps) => (
  <FullPage>
    <MobileContainer>
      <MobileHeader>
        <BackButton onClick={props.onDismiss}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="-89.999 596.249 15.999 15.554"
            fill="#99959d"
          >
            <path
              d="M-923.583-12226.809l0,0-1.413-1.416v0l1.413-1.415,0,0,6.363-6.363,1.413,1.416-5.334,5.334H-909v2h-12.2l5.389,5.387-1.413,1.415Z"
              transform="translate(835 12832.249)"
            />
          </svg>
        </BackButton>
        <HeaderText>
          <Gettext>Select the country code</Gettext>
        </HeaderText>
        <CloseButton onClick={props.onDismiss}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 22 22">
            <g>
              <line
                x2="20"
                y2="20"
                fill="none"
                stroke="#96949b"
                strokeWidth="2"
              />
              <line
                x1="20"
                y2="20"
                fill="none"
                stroke="#96949b"
                strokeWidth="2"
              />
            </g>
          </svg>
        </CloseButton>
      </MobileHeader>
      <SizerContainer>{props.children}</SizerContainer>
      {props.showInfo && (
        <InfoContainer>
          <Info>{props.showInfo}</Info>
        </InfoContainer>
      )}
    </MobileContainer>
  </FullPage>
);

type MenuWrapperProps = {
  ...MobileMenuProps,
  isDesktop: boolean
};

const MenuWrapper = ({ isDesktop, children, ...props }: MenuWrapperProps) =>
  isDesktop ? (
    <Menu className="react-select__menu">{children}</Menu>
  ) : (
    <MobileMenu {...props}>{children}</MobileMenu>
  );

const ListRow = ({
  rowKey, // Unique key within array of rows
  index, // Index of row within collection
  isScrolling, // The List is currently being scrolled
  isVisible, // This row is visible within the List (eg it is not an overscanned row)
  style, // Style object to be applied to row (to position it)
  optionProps,
  ...props
}) => {
  const currentOption = props.options[index];
  const { label, code, phoneCode } = currentOption;

  return (
    <Option
      {...optionProps.innerProps}
      key={label}
      style={style}
      onClick={() => optionProps.setValue(currentOption)}
      isProfile={props.isProfile}
    >
      <OptionContainer>
        <OptionInner>
          <Flag country={code} />
          <OptionLabel>{label}</OptionLabel>
        </OptionInner>
        {code !== "world" && <SelectCode>+{phoneCode}</SelectCode>}
      </OptionContainer>
    </Option>
  );
};

type State = {
  menuIsOpen: boolean
};

class CountrySelect extends React.Component<Props, State> {
  state = {
    menuIsOpen: false
  };

  handleMediaChange = () => {
    if (document.body) {
      if (this.props.desktop === false) {
        document.body.style.overflow =
          this.state.menuIsOpen === true ? "hidden" : "";
      } else {
        document.body.style.overflow = "";
      }
    }
  };

  handleMenuEvent = (menuIsOpen: boolean) => {
    this.setState({ menuIsOpen }, this.handleMediaChange);
  };

  handleChange = (value: *) => {
    this.props.onChange({
      target: {
        name: this.props.name,
        value: value
      }
    });
  };

  componentDidMount() {
    if (this.state.menuIsOpen === true) {
      this.handleMediaChange();
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.desktop !== this.props.desktop) {
      this.handleMediaChange();
    }
  }

  componentWillUnmount() {
    if (document.body) {
      document.body.style.overflow = "";
    }
  }

  render() {
    const isLocked = typeof this.props.onChange !== "function";

    return (
      <ThemeConsumer>
        {theme => (
          <Select
            classNamePrefix="react-select"
            isDisabled={isLocked}
            invalid={this.props.error}
            value={this.props.value}
            options={this.props.options}
            onChange={this.handleChange}
            placeholder={<Placeholder isProfile={this.props.isProfile} />}
            isRtl={theme.direction === "rtl"}
            isLoading={false}
            isClearable={false}
            isSearchable={false}
            isMulti={false}
            ignoreAccents={true}
            ignoreCase={true}
            isProfile={this.props.isProfile}
            isWithdraw={this.props.isWithdraw}
            components={{
              SingleValue: props => (
                <ValueView
                  {...props}
                  isProfile={this.props.isProfile}
                  version={this.props.version}
                />
              ),
              Menu: menuProps => (
                <components.Menu {...menuProps}>
                  <MenuWrapper
                    isDesktop={this.props.desktop}
                    onDismiss={() => menuProps.setValue(this.props.value)}
                    showInfo={this.props.showInfo}
                  >
                    <AutoSizer>
                      {({ width, height }) => (
                        <List
                          width={width}
                          height={height}
                          overscanRowCount={10}
                          rowCount={this.props.options.length}
                          rowHeight={this.props.desktop ? 36 : 46}
                          rowRenderer={rowProps => (
                            <ListRow
                              {...rowProps}
                              rowKey={rowProps.key}
                              {...this.props}
                              optionProps={menuProps}
                            />
                          )}
                          scrollToAlignment="start"
                          scrollToIndex={this.props.options.indexOf(
                            this.props.value
                          )}
                        />
                      )}
                    </AutoSizer>
                  </MenuWrapper>
                </components.Menu>
              )
            }}
            menuIsOpen={this.state.menuIsOpen}
            onMenuOpen={() => this.handleMenuEvent(true)}
            onMenuClose={() => this.handleMenuEvent(false)}
            version={this.props.version}
          />
        )}
      </ThemeConsumer>
    );
  }
}

export default (props: Props) => (
  <Media query="(min-width: 768px)">
    {desktop => <CountrySelect {...props} desktop={desktop} />}
  </Media>
);



// WEBPACK FOOTER //
// ./src/components/ReVerification/CountrySelect.js